import * as React from 'react';
import styled from 'styled-components';

import type { ISeriesConfig } from './types';

interface IProps {
  /** Series configurations to display in the legend */
  series: ISeriesConfig[];
  /** Text color for legend labels */
  textColor?: string;
  /** Set of indices for hidden series */
  hiddenIndices: Set<number>;
  /** Callback when a legend item is clicked to toggle visibility */
  onToggle: (index: number) => void;
}

/**
 * Custom chart legend component that renders legend items with
 * styled circular color indicators with dark outlines.
 * Supports click-to-toggle visibility with strikethrough styling.
 */
const ChartLegend = (props: IProps) => {
  const textColor = props.textColor ?? '#333';

  return (
    <LegendContainer>
      {props.series.map((s, idx) => {
        const isHidden = props.hiddenIndices.has(idx);
        return (
          <LegendItem key={idx} onClick={() => props.onToggle(idx)}>
            <ColorIndicator color={s.color} $hidden={isHidden} />
            <LegendLabel style={{ color: textColor }} $hidden={isHidden}>
              {s.label}
            </LegendLabel>
          </LegendItem>
        );
      })}
    </LegendContainer>
  );
};

const LegendContainer = styled.div`
  display: flex;
  justify-content: center;
  gap: 16px;
  padding: 8px 0;
`;

const LegendItem = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  cursor: pointer;
  user-select: none;

  &:hover {
    opacity: 0.8;
  }
`;

const ColorIndicator = styled.div<{ color: string; $hidden: boolean }>`
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background-color: ${p => p.color};
  box-shadow: 0 0 0 1px rgba(0, 0, 0, 0.3);
  opacity: ${p => p.$hidden ? 0.4 : 1};
`;

const LegendLabel = styled.span<{ $hidden: boolean }>`
  font-size: 12px;
  text-decoration: ${p => p.$hidden ? 'line-through' : 'none'};
  opacity: ${p => p.$hidden ? 0.6 : 1};
`;

export { ChartLegend }
